import React, { useMemo, useRef } from 'react';

import allImgPaths from 'assets/allImgPaths';
import { IconButton, Text } from 'components';
import Dropdown from 'components/common/DropDown';
import { Skeleton } from 'components/Skeleton';
import { useWindowSize } from 'hooks';

interface PaginationPropsType {
  pageNumber: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<number>;
  totalCount: number;
  defaultSize: number;
  onDownload?: () => void;
}

// Separate component for page button
const PageButton: React.FC<{
  number: number;
  isActive: boolean;
  onClick: () => void;
  width: string;
}> = ({ number, isActive, onClick, width }) => (
  <button
    onClick={onClick}
    className={`${width} h-[30px] md:h-10 rounded text-xs md:text-sm flex justify-center items-center text-white ${
      isActive
        ? 'border border-solid border-white'
        : 'border border-solid border-[#FFFFFF14] bg-[linear-gradient(90deg,_#ffffff0a_0%,_#ffffff00_100%)]'
    }`}
  >
    {number}
  </button>
);

// Separate component for ellipsis
const Ellipsis: React.FC = () => (
  <span className="text-white border border-solid border-[#FFFFFF14] bg-[linear-gradient(90deg,_#ffffff0a_0%,_#ffffff00_100%)] w-7 sm:w-[42px] h-7 md:h-10 rounded text-xs md:text-sm flex justify-center items-center">
    ...
  </span>
);

const Pagination: React.FC<PaginationPropsType> = ({
  pageNumber,
  setPageNumber,
  setPageSize,
  totalCount,
  defaultSize,
  onDownload,
}) => {
  const { isMobile } = useWindowSize();
  const options = useRef(['10', '15', '20']);
  const totalPages = Math.ceil(totalCount / defaultSize);

  // Utility function to get button width based on number length
  const getButtonWidth = (number: number): string => {
    const digits = number.toString().length;
    if (isMobile) {
      if (digits <= 2) return 'w-[28px]';
      if (digits === 3) return 'w-[36px]';
      if (digits === 4) return 'w-[44px]';
      if (digits === 5) return 'w-[52px]';
      return 'w-[60px]'; // For 6+ digits
    }
    if (digits <= 2) return 'w-[36px]';
    if (digits === 3) return 'w-[52px]';
    if (digits === 4) return 'w-[68px]';
    if (digits === 5) return 'w-[84px]';
    return 'w-[100px]'; // For 6+ digits
  };

  // Calculate pagination range
  const paginationRange = useMemo(() => {
    const maxVisiblePages = isMobile ? 3 : 4;
    let startPage = pageNumber;

    if (pageNumber === totalPages) {
      startPage = Math.max(1, pageNumber - 3);
    } else if (pageNumber > 1) {
      startPage = Math.max(1, pageNumber - 1);
    }
    const endPage = Math.min(startPage + maxVisiblePages - 1, totalPages);
    return { startPage, endPage };
  }, [pageNumber, totalPages, isMobile]);

  // Render page numbers
  const renderPageNumbers = () => {
    const pages = [];
    const { startPage, endPage } = paginationRange;

    // First page
    if (startPage > 1) {
      pages.push(
        <PageButton
          key={1}
          number={1}
          isActive={pageNumber === 1}
          onClick={() => setPageNumber(1)}
          width={getButtonWidth(1)}
        />
      );
      if (startPage > 2) {
        pages.push(<Ellipsis key="ellipsis1" />);
      }
    }

    // Middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PageButton
          key={i}
          number={i}
          isActive={pageNumber === i}
          onClick={() => setPageNumber(i)}
          width={getButtonWidth(i)}
        />
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<Ellipsis key="ellipsis2" />);
      }
      pages.push(
        <PageButton
          key={totalPages}
          number={totalPages}
          isActive={pageNumber === totalPages}
          onClick={() => setPageNumber(totalPages)}
          width={getButtonWidth(totalPages)}
        />
      );
    }

    return pages;
  };

  // Results text
  const resultsText = useMemo(() => {
    const start = defaultSize * (pageNumber - 1) + 1;
    const end = defaultSize * pageNumber >= totalCount ? totalCount : defaultSize * pageNumber;
    return `${start} - ${end} results of ${totalCount} shown`;
  }, [pageNumber, defaultSize, totalCount]);

  return (
    <div className="flex justify-between flex-wrap items-start md:items-center gap-5">
      <div className="flex flex-col md:flex-row gap-2 md:gap-[10px] items-start md:items-center md:flex-wrap">
        <div className="flex gap-[10px] items-center">
          <div className="flex gap-1.5 sm:gap-2 items-center">
            <Skeleton.Loader className="h-full w-full" containerClassName="h-10 w-10 rounded-full">
              <IconButton
                icon={allImgPaths.leftCaretIcon}
                onClick={() => setPageNumber((prev) => prev - 1)}
                disabled={pageNumber === 1 || totalCount === 0}
              />
            </Skeleton.Loader>
            <Skeleton.Loader
              containerClassName="md:w-[149px] md:h-6 w-[127px] h-5"
              className="w-full h-full"
            >
              <div className="flex gap-1 sm:gap-2 items-center">{renderPageNumbers()}</div>
            </Skeleton.Loader>
            <Skeleton.Loader className="h-full w-full" containerClassName="h-10 w-10 rounded-full">
              <IconButton
                icon={allImgPaths.rightCaretIcon}
                onClick={() => setPageNumber((prev) => prev + 1)}
                disabled={pageNumber === totalPages || totalCount === 0}
              />
            </Skeleton.Loader>
          </div>
        </div>
        <div className="text-white">
          <Skeleton.Loader
            containerClassName="md:w-[149px] md:h-6 w-[127px] h-5"
            className="w-full h-full"
          >
            <span className="text-xs md:text-sm whitespace-nowrap">{resultsText}</span>
          </Skeleton.Loader>
        </div>
      </div>
      <div className="flex gap-[15px] items-center">
        {onDownload && (
          <Skeleton.Loader className="h-full w-full self-center" containerClassName="h-6 !flex">
            <button
              onClick={onDownload}
              className="flex gap-2 justify-center items-center rounded-3xl border border-solid border-[#FFFFFF14] 
                       bg-[linear-gradient(90deg,_#ffffff0a_0%,_#ffffff00_100%)] px-6 h-10"
            >
              <img src={allImgPaths.downloadIcon} alt="Download" />
              <span className="text-white text-xs md:text-sm">Download</span>
            </button>
          </Skeleton.Loader>
        )}
        <Skeleton.Loader
          className="h-full w-full self-center"
          containerClassName="w-[34px] h-6 !flex"
        >
          <Text extraClass="text-xs md:text-sm">Rows</Text>
        </Skeleton.Loader>
        <Skeleton.Loader className="h-full w-full" containerClassName="w-[57px] h-10">
          <Dropdown
            options={options.current}
            onSelect={(selectedOption) => setPageSize(Number(selectedOption))}
            defaultSelected={defaultSize.toString()}
            classNames={{
              base: 'relative',
              selector:
                'shadow-none border border-solid border-[#FFFFFF14] bg-[linear-gradient(90deg,_#ffffff0a_0%,_#ffffff00_100%)]',
              optionContainer: 'px-3 py-0',
              container: 'bottom-[42px]',
            }}
          />
        </Skeleton.Loader>
      </div>
    </div>
  );
};

export default Pagination;
